import { ProgramNode, OnsiteScheduleEntry, DLLNode, OnsiteActivity, OnsiteSchedule, ProgramNodeMap, ProgramNodeDateMap, OnsiteProgram, sortActivityBlock, OnsiteSession, OnsiteTimelineBrick, OnsiteAccess, OnsiteClear } from '../store/onsite.data'
import { listOfIdsToIndexIdMap, str2Date, date_id, stripBackslashes, timeConvert, getDefaultTimeZoneDetails, justTimeToHHMM, HHMMToMs } from '../helpers/helpers'
import { DAY_MS } from '../helpers/constants'
import { Program, Setup } from '../store/programs.data'
import { flatten } from './../helpers/helpers';

const getHeadNode = (node: ProgramNode) => node.next ? getHeadNode(node.next) : node
const getTailNode = (node: ProgramNode) => node.prev ? getTailNode(node.prev) : node

const addScheduleNodeToBeginningOrEnd = (day, node: ProgramNode, incomingNode) => {
	// adds a node to the head (and updates the old head) if day is positive, or to the tail if negative.  This mutates the double linked list in place (the head or tail node + the incoming node) and also assumes that things are coming in in the right order
	if (day > 0) {
    const headNode = getHeadNode(node)
		incomingNode.prev = headNode
		headNode.next = incomingNode
  } else { // go to tail
    const tailNode = getTailNode(node)
		tailNode.prev = incomingNode
		incomingNode.next = tailNode
	}
}

const addSubschedule = (node: ProgramNode, scheduleTemplate, subscheduleByIdMap, order) => {
	const { extension, day } = node.schedule
	const subSchedule = { ...scheduleTemplate, sessions: [], extension, order, day }
	const id = (extension ? "E" + extension : "P" + day) + scheduleTemplate.id
	subscheduleByIdMap[id] = subSchedule
	node.subschedules.push(subSchedule)
}

export const makeDateNodesAndDateEntries = datesArray => {
  datesArray.sort()
	const indexToScheduleDateMap: { [id:string]: string } = listOfIdsToIndexIdMap(datesArray)
	let tempDateId = "0"
	const dateEntries: OnsiteScheduleEntry[][] = []

	let headDateNode:DLLNode<string>
	const dateNodesByDate: Map<string, DLLNode<string>> = new Map()
	for (const date of datesArray) {
		const newDateNode:DLLNode<string> = {
			data: date
		}
		if (headDateNode) {
			newDateNode.prev = headDateNode
			headDateNode.next = newDateNode
		}
		dateNodesByDate.set(date, newDateNode)
		headDateNode = newDateNode
	}

	datesArray.forEach(id => {
		if (str2Date(id).getTime() - str2Date(tempDateId).getTime() > DAY_MS || !dateEntries.length) {
			dateEntries.push([{ id, date: str2Date(id) }])
		} else {
      dateEntries[dateEntries.length - 1].push({ id, date: str2Date(id) })
		}
		tempDateId = id
  })
  return { dateNodesByDate, dateEntries, indexToScheduleDateMap }
}

export const getOnsiteSchedules = (activities: OnsiteActivity[], schedules: OnsiteSchedule[], programs: Program[], activitiesLoaded: boolean) => {
  // console.log({ programs, schedules, activities })
  // console.log("getOnsiteSchedules called", programs)
	const scheduleDates: Set<string> = new Set()
	const programNodeCentersById: ProgramNodeMap = new Map()
	const programNodesByDate: ProgramNodeDateMap = {}
	const subscheduleByIdMap: { [id: string]: OnsiteSchedule } = {}
  const programsById: { [id:string]: Program } = {}
  const subscheduleIdsByProgramId: { [id: string]: Set<string> } = {}
  
  // add all program day session groups in as program schedules

  if (programs) {
    const datedPrograms = programs.filter(p => p.date)
    datedPrograms.forEach(({ id }) => subscheduleIdsByProgramId[id] = new Set())
    datedPrograms.forEach(p => {
      programsById[p.id] = p
      const { title, location, venueId, labels, order, performerTags, sponsorTags, exhibitorTags, workforceTags, timeZoneDetails, resourceTags, setups, attendeeTags } = p
      const makeSetupBrick = ({ id, name }: Setup, type: 'access' | 'clear', time: string): OnsiteAccess | OnsiteClear => ({
        id, title: `${name}: ${type}`, type, time, description: ''
      })

      const addProgramScheduleAndNode = (currentDay: number, sessions: OnsiteSession[] = [], session_labels: string[] = []) => {
        const dateId = date_id(new Date(new Date(p.date).getTime() + currentDay * DAY_MS))
        // console.log({ dateId, date: p.date })
        scheduleDates.add(dateId)

        const sameDayAccessSetups = Object.values(setups || {}).filter(s => s.accessDay === currentDay && s.access)
        const sameDayClearSetups = Object.values(setups || {}).filter(s => s.clearDay === currentDay && s.clear)

        const setupBricks: OnsiteTimelineBrick[] = []

        // console.log(dateId, { sameDayAccessSetups, sameDayClearSetups, currentDay })
        sameDayAccessSetups.forEach(setup => setupBricks.push(makeSetupBrick(setup, 'access', justTimeToHHMM(setup.access, true))))
        sameDayClearSetups.forEach(setup => setupBricks.push(makeSetupBrick(setup, 'clear', justTimeToHHMM(setup.clear, true))))
        const allBricks: OnsiteTimelineBrick[] = [...sessions, ...setupBricks ]
        allBricks.sort((a,b) => {
          const aStart = HHMMToMs(a.start)
          const bStart = HHMMToMs(b.start)
          if (aStart < bStart) return -1
          else if (bStart > aStart) return 1
          // times are equal
          if (a.type === 'access') return -1
          if (b.type === 'clear') return -1
          if (b.type === 'access') return 1
          if (a.type === 'clear') return 1
          if (a.type === 'session' && b.type === 'session') return a.scheduleOrder - b.scheduleOrder
          return -1
        })

        const programSchedule: OnsiteProgram = {
          type: 'program',
          subType: 'program',
          scheduleId: p.id,
          session_labels,
          sessions: allBricks,
          timeZoneId: timeZoneDetails?.timeZoneId || getDefaultTimeZoneDetails().timeZoneId,
          venueId,
          date: dateId,
          day: currentDay,
          title, location, labels, order, performerTags, sponsorTags, exhibitorTags, workforceTags, resourceTags, attendeeTags
        }

        const node: ProgramNode = {
          schedule: programSchedule,
          day: currentDay,
          extension: false,
          subschedules: []
        }
    
        if (currentDay === 0) {
          programNodeCentersById.set(p.id, node)
        } else if (currentDay < 0) {
          let headNode = programNodeCentersById.get(p.id)
          while (headNode.prev) {
            headNode = headNode.prev
          }
          node.next = headNode
          headNode.prev = node
        } else {
          let headNode = programNodeCentersById.get(p.id)
          while (headNode.next) {
            headNode = headNode.next
          }
          node.prev = headNode
          headNode.next = node
        }
        if (!programNodesByDate[dateId]) programNodesByDate[dateId] = new Map()
        programNodesByDate[dateId].set(p.id, node)
      }

      let programDay: number
      p.sessions.forEach(sess => {
        const sessions: OnsiteSession[] = sess.sessions.map(id => ({ ...p.sessions_obj[id], id, labels: p.sessions_obj[id].labels || [] }))
        const session_labels: Set<string> = new Set()
        sess.sessions.forEach(id => {
          const sessionLabels = p.sessions_obj[id].labels || [] 
          sessionLabels.forEach(l => session_labels.add(l))
        })
        programDay = sess.day - 1
        addProgramScheduleAndNode(programDay, sessions, Array.from(session_labels))
      })

      const setupDays = Array.from(new Set(flatten(Object.values(p.setups || {}).map(s => [s.accessDay, s.clearDay]))))
      const negativeSetupDays = setupDays.filter(d => d < 0).sort((a,b) => b - a)
      const positiveSetupDays = setupDays.filter(d => d > programDay).sort()
      // console.log({ negativeSetupDays, positiveSetupDays, setupDays })
      positiveSetupDays.forEach(d => addProgramScheduleAndNode(d))
      negativeSetupDays.forEach(d => addProgramScheduleAndNode(d))
    })
  }

  schedules.forEach(({ id, scheduleId }) => !!subscheduleIdsByProgramId[scheduleId] && subscheduleIdsByProgramId[scheduleId].add(id))
	const extensionSortedSchedules = schedules.filter(sch => sch.extension)
	extensionSortedSchedules.sort((a, b) => Math.abs(a.day) - Math.abs(b.day)) // want them to be sorted like -1,1,2,-2,3,-3 so that the tails go backwards one by one and the heads go forward one by one, linking to the previous one or next one correctly


	// Find extension schedules attached to a program and treat like programs. These will be in the first lanes like program themselves
	extensionSortedSchedules.filter(sch => sch.extension && sch.day).forEach(sch => {
		const program = programsById[sch.scheduleId]
		if (program) {
      const dateId = date_id(new Date(new Date(program.date).getTime() + (sch.day > 0 ? (program.days + sch.day - 1) : sch.day) * DAY_MS))
      // console.log({ dateId, date: program.date })
      scheduleDates.add(dateId)

      const { title, location, venueId, labels, order, performerTags, sponsorTags, exhibitorTags, workforceTags, timeZoneDetails, secondaryTimeZoneDetails, resourceTags, attendeeTags } = program
			const programExtensionSchedule: OnsiteProgram = {
				type: 'program',
				subType: 'programExtension',
				scheduleId: program.id,
				id: sch.id,
        sessions: [],
        timeZoneId: timeZoneDetails?.timeZoneId || getDefaultTimeZoneDetails().timeZoneId,
				session_labels: [],
				date: dateId,
				day: sch.day,
        extension: sch.day,
        title, location, venueId, labels, order, performerTags, sponsorTags, exhibitorTags, workforceTags, resourceTags, attendeeTags
			}

			const node: ProgramNode = {
				schedule: programExtensionSchedule,
				day: sch.day,
				extension: true,
				subschedules: []
			}

			const centerNode = programNodeCentersById.get(program.id)
			if (!centerNode) {
				console.log("Extension schedule for program missing actual program schedule...", { program, programs })
				return
			}
			addScheduleNodeToBeginningOrEnd(sch.day, centerNode, node)
			if (!programNodesByDate[dateId]) programNodesByDate[dateId] = new Map()
			programNodesByDate[dateId].set(program.id, node)

		}
	})


	// find any subschedules of both program schedules / program extension schedules <== THIS IS A FIFTH STEP THAT DIDN'T EXIST BEFORE
	const programSubSchedules = schedules.filter(sch => sch.id && !isNaN(sch.order) && sch.id !== sch.scheduleId)
	programSubSchedules.sort((a, b) => a.order - b.order)
	programSubSchedules.forEach((sch, index) => {
		const program = programsById[sch.scheduleId]
		if (!program) return

		const scheduleTemplate = {
			type: 'SubSchedule',
			scheduleId: sch.scheduleId,
			title: sch.title,
			id: sch.id,
			day: sch.day,
			extension: sch.extension
		}

		const centerNode = programNodeCentersById.get(sch.scheduleId)
		if (!centerNode) return
		let headNode = centerNode
		let tailNode = centerNode.prev

		while (headNode) {
			addSubschedule(headNode, scheduleTemplate, subscheduleByIdMap, index+1)
			headNode = headNode.next
		}
		while (tailNode) {
			addSubschedule(tailNode, scheduleTemplate, subscheduleByIdMap, index+1)
			tailNode = tailNode.prev
		}

	})

	// process standalone schedules to make sure they are displayed after program
	const standaloneSchedules = schedules.filter(sch => sch.scheduleId === sch.id)
	standaloneSchedules.forEach(sch => {

    const schDate = stripBackslashes(sch.date)// I have no idea why dates of format YYYYMM/DD were making it into these schedules, but for now just going to use this hack-fix
		scheduleDates.add(schDate)

    const timeZoneId = sch.timeZoneId || getDefaultTimeZoneDetails().timeZoneId

		const standaloneSchedule: OnsiteProgram = {
			type: 'schedule',
			subType: 'standalone',
			scheduleId: sch.scheduleId,
			id: sch.id,
			title: sch.title,
			location: sch.location,
      sessions: [],
      timeZoneId,
			labels: [],
			session_labels: [],
			venueId: sch.venueId,
			date: schDate,
			order: sch.order,
			day: 0
		}

		subscheduleByIdMap[sch.id] = standaloneSchedule

		const node: ProgramNode = {
			schedule: standaloneSchedule,
			day: 0,
			extension: false,
			standalone: true,
			subschedules: []
		}

		programNodeCentersById.set(sch.scheduleId, node)
		if (!programNodesByDate[schDate]) programNodesByDate[schDate] = new Map()
		programNodesByDate[schDate].set(sch.scheduleId, node)


	})



  // Find all standalone extension schedules finally
  const orphanedSchedules = []
	extensionSortedSchedules.filter(sch => !isNaN(sch.day) && !programs.find(p => p.id === sch.scheduleId)).forEach(sch => {

		const standaloneCenterNode = programNodeCentersById.get(sch.scheduleId)
		if (!standaloneCenterNode) {
      if (programs.length) orphanedSchedules.push({ sch, schedules, programNodeCentersById })
      return
		}
		const core_sch = standaloneCenterNode.schedule
		const dateId = date_id(new Date(str2Date(core_sch.date).getTime() + sch.day * DAY_MS))
		scheduleDates.add(dateId)
		const standaloneExtensionSchedule: OnsiteProgram = {
			type: 'schedule',
			subType: 'standaloneExtension',
			scheduleId: core_sch.scheduleId,
			id: sch.id,
			title: core_sch.title,
			location: core_sch.location,
			venueId: core_sch.venueId,
      sessions: [],
      timeZoneId: core_sch.timeZoneId || getDefaultTimeZoneDetails().timeZoneId,
			labels: [],
			session_labels: [],
			date: dateId,
			order: core_sch.order,
			extension: sch.day,
			day: sch.day
		}

		standaloneCenterNode.subschedules.push(standaloneExtensionSchedule)
		subscheduleByIdMap[sch.id] = standaloneExtensionSchedule

		const node: ProgramNode = {
			schedule: standaloneExtensionSchedule,
			day: sch.day,
			extension: true,
			standalone: true
		}
		const centerNode = programNodeCentersById.get(sch.scheduleId)
		if (!centerNode) {
      if (!programs.length) return // programs haven't loaded yet
			console.log("Extension schedule for STANDALONE program missing actual program schedule...", { sch, programs })
			return
		}
		addScheduleNodeToBeginningOrEnd(sch.day, centerNode, node)
		if (!programNodesByDate[dateId]) programNodesByDate[dateId] = new Map()
		programNodesByDate[dateId].set(sch.scheduleId, node)

	})

  if (orphanedSchedules.length) {
    // this is crashing the console it logs so much // console.log("There are the following orphaned schedules.  They should be deleted upon the next full update to onsite schedules: ", { orphanedSchedules })
  }

  const usedScheduleIds: Set<string> = new Set()
	activities.forEach( (act: OnsiteActivity) => {
		let schedule: OnsiteSchedule
		if (subscheduleByIdMap[act.linkId]) { // standalone schedule activities
      schedule = subscheduleByIdMap[act.linkId]
      usedScheduleIds.add(act.linkId)
		} else {
			const { extension, day, linkId } = act
			const id = (extension ? "E" : "P") + day + linkId
      schedule = subscheduleByIdMap[id]
			if (!schedule) {
				return
      }
      usedScheduleIds.add(id)
		}
		schedule.sessions.push({
			...act,
			schedule_title: schedule.title,
			scheduleOrder: schedule.order
		})
  })

  usedScheduleIds.forEach(id => {
    activities = subscheduleByIdMap[id].sessions
    sortActivityBlock(activities)
    activities.forEach(a => a.justTime = timeConvert(a.time))
  })

	const datesArray: string[] = Array.from(scheduleDates)
  const { dateNodesByDate, dateEntries, indexToScheduleDateMap } = makeDateNodesAndDateEntries(datesArray)

  const ready = !!programs?.length && (!!activities?.length || !!schedules?.length || activitiesLoaded) 
	return ({ indexToScheduleDateMap, dateEntries, subscheduleByIdMap, programNodeCentersById, programNodesByDate, dateNodesByDate, subscheduleIdsByProgramId, ready })
}